import { useState, useEffect, useRef } from 'react';
import { PAYMENT_FALLBACK_ENABLED } from 'src/config/paymentFallback';
import { PatientProfileResponseSchema } from './components/Stepper/steps/schemas';
import { useRoutes, useLocation, useNavigate } from 'react-router-dom';
import router from 'src/router';
import 'src/styles/global.css';
import StepperComponent from './components/Stepper';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ThemeProviderWrapper from './theme/ThemeProvider';
import { RoleProvider } from 'src/contexts/RoleContext/RoleContext';
import { RegionProvider } from 'src/contexts/RegionContext/RegionContext';
import '@aws-amplify/ui-react/styles.css';
import { Amplify } from 'aws-amplify';
import awsExports from './aws-exports';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPatientProfile, login, logout } from './store/reducers/authReducer';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser } from 'aws-amplify/auth';
import { RootState, AppDispatch } from './store/store';
import CustomAuthenticator from './authenticator/customAuthenticator';
import CustomDoctorAuthenticator from './authenticator/customDoctorAuthenticator';
import CookieBanner from './components/CookieBanner';
import SuspenseLoader from './components/SuspenseLoader';
import {
    syncCookieConsentFromAccount,
    retryCookieConsentSync,
    clearSessionScopedStorage,
} from './config/consent/cookieConsentStore';
Amplify.configure(awsExports);

function App() {
    const content = useRoutes(router);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const [loading, setLoading] = useState<boolean>(false);
    const paymentStatus = useSelector((state: RootState) => state.auth.paymentStatus);
    // One profile read per session start, whichever path gets there first.
    const profileRequested = useRef(false);
    const [revalidating, setRevalidating] = useState(false);

    const handleLogin = async (): Promise<string | null> => {
        profileRequested.current = true;
        setLoading(true);
        try {
            const user = await getCurrentUser();
            if (user) {
                const result = await dispatch(fetchPatientProfile());
                const parsed = PatientProfileResponseSchema.safeParse(result.payload);
                const payload = parsed.success ? parsed.data : {
                    profile: null,
                    hasPaidBefore: false,
                    itemType: 'patient' as const,
                    pk: '',
                    paymentStatus: '',
                    onboardingCompleted: false,
                };
                const { profile, hasPaidBefore, itemType, pk, paymentStatus, onboardingCompleted } = payload;
                dispatch(login({ username: user.username, hasPaidBefore, profile, itemType, pk, paymentStatus, onboardingCompleted }));
                return itemType;
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setLoading(false);
        }
        return null;
    };

    useEffect(() => {
        const hubListener = Hub.listen('auth', (data) => {
            const { payload } = data;
            if (payload.event === 'signedIn') {
                // Attach the pre-login cookie choice to the account and pull
                // down whichever decision is newer, so a preference set on one
                // device is not re-asked on the next. Self-contained and silent
                // on failure — the local copy already governs behaviour.
                //
                // Patients only: both calls resolve the account to a patient
                // record server-side, so staff have nothing for them to attach
                // to. Waiting for the profile is what tells us which we are.
                handleLogin().then((itemType) => {
                    if (itemType === 'patient') syncCookieConsentFromAccount();
                });
            } else if (payload.event === 'signedOut') {
                profileRequested.current = false;
                dispatch(logout());
                // Payment status and staff search history (which can contain
                // patient names) must not survive on a shared workstation.
                clearSessionScopedStorage();
            }
        });
        return () => {
            hubListener();
        };
    }, [dispatch]);

    // A reload, a new tab, or the return from Stripe restores the session from
    // localStorage, but Amplify fires no 'signedIn' for an existing session — so
    // nothing re-read the profile and the payment gate kept acting on a stale copy
    // (paid patients were left on the Select Service step). Dispatches the thunk
    // directly rather than handleLogin: a failed request then leaves the persisted
    // state alone instead of resetting it to an empty patient.
    useEffect(() => {
        if (!isAuthenticated || profileRequested.current) return;
        profileRequested.current = true;
        setRevalidating(true);
        dispatch(fetchPatientProfile()).finally(() => setRevalidating(false));
    }, [isAuthenticated, dispatch]);

    // Retried once per app start; both no-op when there is nothing pending.
    useEffect(() => {
        retryCookieConsentSync();
    }, []);


    useEffect(() => {
        if (isAuthenticated && location.pathname === '/provider') {
            navigate('/');
        }
    }, [isAuthenticated, location.pathname, navigate]);

    const role = useSelector((state: RootState) => state.auth.itemType);
    const hasPaidBefore = useSelector((state: RootState) => state.auth.hasPaidBefore);
    const onboardingCompleted = useSelector((state: RootState) => state.auth.onboardingCompleted);
    const isPatientRole = role !== 'doctor' && role !== 'admin' && role !== 'nurse';
    // Server-owned gate, and only that. The old `stepper_completed` localStorage
    // fallback could be set by hand in DevTools to skip consent entirely, so it
    // is gone: if the server has not recorded onboarding, onboarding runs again.
    // Payment is the gate. `profile_sent` means UNPAID, and finishing the
    // onboarding steps is not payment either — neither opens the app any more.
    const hasPaid = hasPaidBefore || paymentStatus === 'paid';
    // Temporary: while checkout is broken on live, a patient who finished
    // onboarding is let through unpaid. See src/config/paymentFallback.ts.
    const paymentBypass = PAYMENT_FALLBACK_ENABLED
        && (onboardingCompleted || paymentStatus === 'profile_sent');
    const needsStepper = isAuthenticated && isPatientRole && !hasPaid && !paymentBypass
        && !location.pathname.startsWith('/services/')
        && location.pathname !== '/payment-success'
        && location.pathname !== '/payment-failed';
    const isPublicRoute = ['/privacy-policy', '/terms-conditions'].includes(location.pathname)
        || location.pathname.startsWith('/v2');

    return (
        <RegionProvider>
            <RoleProvider>
                <ThemeProviderWrapper>
                    <ToastContainer position="top-right" autoClose={5000} style={{ zIndex: 2147483647 }} />
                    <CookieBanner />
                    {location.pathname === '/provider' ? (
                        <CustomDoctorAuthenticator authLoading={loading} showLoginOnly />
                    ) : !isAuthenticated && !isPublicRoute ? (
                        <CustomAuthenticator authLoading={loading} />
                    ) : (
                        <>
                            {/* Unpaid patients get onboarding ONLY — the app's routes are not
                                mounted behind it, so nothing loads before payment. */}
                            {needsStepper ? (
                                // Hold onboarding until the fresh profile answers, so a
                                // patient who has already paid never sees it flash.
                                revalidating ? <SuspenseLoader /> : <StepperComponent />
                            ) : (
                                <div className="flex-1 flex flex-col min-h-0">{content}</div>
                            )}
                        </>
                    )}
                </ThemeProviderWrapper>
            </RoleProvider>
        </RegionProvider>
    );
}

export default App;
